import { appNow } from './appClock'
// ============================================
// candleStore.js
// IndexedDB-backed persistence for live-built footprint candles.
//
// Why this exists: without it, closing the tab erases every candle the
// chart has accumulated. With it, the chart paints from local cache on
// reload before the WebSocket has even connected, and the user gets
// continuity across sessions.
//
// What persists: only LIVE-built candles (the ones with footprint
// data). Historical-seed candles from GeckoTerminal/Hyperliquid are
// re-fetched on each session — we don't duplicate work the backend
// already does, and they have no per-level data to preserve anyway.
//
// Key shape: ['cacheId', 'startTime'] where cacheId encodes
//   `${tokenKey}|${intervalSec}|${bucketSize}`. Different bucket sizes
//   live in different caches so you never see mismatched granularity
//   on reload. tokenKey is prefixed `sol:` or `hl:` to keep namespaces
//   from colliding (a Solana mint and an HL coin like "ETH" otherwise
//   could clash if a memecoin had a 3-letter mint, hypothetically).
//
// What's stored: Float64Array `_native` (stepIdx+buy+sell triplets) plus
// `_nativeBucket` (the bucket size stepIdx is denominated in) plus scalar
// OHLC fields. IDB structured-clone handles Float64Array natively.
// _liveLevels (the Map form) is stripped — every reader uses
// candleHelpers.forEachLevel which transparently handles native-only candles.
//
// Writes are batched on a 5s timer to avoid spamming the disk during
// rapid candle closures (e.g., 30s intervals on a high-flow coin).
// Best-effort flush on tab close — IDB transactions can race the
// pagehide event so we don't promise durability there, but most
// modern browsers will let it complete.
// ============================================

const DB_NAME = 'solcheddar'
const DB_VERSION = 2
const STORE_NAME = 'candles'            // legacy store — kept in the schema so
                                        // existing DBs open without a version bump;
                                        // no code reads or writes it anymore
const TRADES_STORE_NAME = 'trades'
const TRADES_FLUSH_INTERVAL_MS = 10000   // trades flush slower (heavier writes)
const TRADES_RETAIN_MS = 4 * 60 * 60 * 1000  // 4h per coin — TF rebuilds replay these; 30min silently amputated everything older

let _dbPromise = null
const _tradesFlushQueue = new Map()      // tokenKey → trades array reference
let _tradesFlushTimer = null

const _openDb = () => {
  if (_dbPromise) return _dbPromise
  if (typeof indexedDB === 'undefined') {
    _dbPromise = Promise.reject(new Error('IndexedDB unavailable'))
    return _dbPromise
  }
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onerror = () => reject(req.error)
    req.onblocked = () => reject(new Error('IDB open blocked'))
    req.onsuccess = () => resolve(req.result)
    req.onupgradeneeded = (e) => {
      // Handles both fresh installs (no stores) and v1→v2 (candles exists,
      // add trades). onupgradeneeded runs once per version step inside a
      // single open, so checking objectStoreNames covers both cases.
      const db = e.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: ['cacheId', 'startTime'],
        })
        store.createIndex('cacheId', 'cacheId', { unique: false })
      }
      if (!db.objectStoreNames.contains(TRADES_STORE_NAME)) {
        db.createObjectStore(TRADES_STORE_NAME, { keyPath: 'tokenKey' })
      }
    }
  })
  return _dbPromise
}

// Resolve tokenKey from the chart's current mode. Pass the active
// hlCoin (or null/empty) and the current Solana mint.
export const resolveTokenKey = (hlCoin, solMint) => {
  if (hlCoin)   return `hl:${hlCoin}`
  if (solMint)  return `sol:${solMint}`
  return null
}

// ============================================
// Raw trade buffer persistence
//
// Stored in the same IDB database as candles, separate object store
// (`trades`). One record per tokenKey holding a Float64Array of
// interleaved [t0, mcap0, amount0, sign0, t1, ...] where sign = ±1 for
// buy/sell. 32 bytes per trade × ~50/sec × 30 min ≈ 2.9 MB per coin.
//
// Restored on coin subscribe so bucket-finer flips and timeframe
// changes have raw data to rebuild from instead of already-aggregated
// candles. Flush is a separate 10s timer; persistTrades stores a live
// reference, so calling it 100× between flushes costs only 100 Map
// writes and one disk transaction.
// ============================================

const _unmarshalTrades = (arr) => {
  const out = []
  if (!arr || arr.length === 0) return out
  const cutoff = appNow() - TRADES_RETAIN_MS
  for (let i = 0; i < arr.length; i += 4) {
    const time = arr[i]
    if (time < cutoff) continue
    out.push({
      time,
      tradeMcap: arr[i + 1],
      amount:    arr[i + 2],
      side:      arr[i + 3] > 0 ? 'buy' : 'sell',
    })
  }
  return out
}

// Normalize whatever persistTrades was handed into the interleaved
// [t, mcap, amount, sign] Float64 layout the store speaks. Accepts:
//   • a TradeBuffer      (columnar in-memory buffer) → toFloat64()
//   • a Float64Array     (already interleaved snapshot) → as-is
//   • an object array    ([{time,tradeMcap,amount,side}, ...]) → marshalled
// All three yield byte-identical values for the same trade, so the dedup
// keys computed downstream match across sources and the append-only union
// never double-stores a trade.
const _toInterleaved = (trades) => {
  if (!trades) return new Float64Array(0)
  if (typeof trades.toFloat64 === 'function') return trades.toFloat64()
  if (trades instanceof Float64Array) return trades
  if (ArrayBuffer.isView(trades)) return Float64Array.from(trades)
  // object array
  const n = trades.length
  const f = new Float64Array(n * 4)
  for (let i = 0; i < n; i++) {
    const t = trades[i]
    const o = i * 4
    f[o] = t.time
    f[o + 1] = t.tradeMcap
    f[o + 2] = t.amount
    f[o + 3] = t.side === 'buy' ? 1 : -1
  }
  return f
}

// Public: load persisted trade buffer for a coin, trimmed to retention
// window. Resolves to [] on any error.
export const loadTrades = async (tokenKey) => {
  if (!tokenKey) return []
  try {
    const db = await _openDb()
    const rec = await new Promise((resolve, reject) => {
      const tx = db.transaction(TRADES_STORE_NAME, 'readonly')
      const req = tx.objectStore(TRADES_STORE_NAME).get(tokenKey)
      req.onsuccess = () => resolve(req.result)
      req.onerror   = () => reject(req.error)
    })
    if (!rec || !rec.trades) return []
    return _unmarshalTrades(rec.trades)
  } catch (err) {
    console.warn('[candleStore] loadTrades failed:', err)
    return []
  }
}

// Public: queue this token's trade buffer for batched flush. Fire-
// and-forget. Holds a reference, not a snapshot — the eventual flush
// captures the freshest state. The value may be a TradeBuffer (the
// live columnar buffer — flush snapshots it via toFloat64 at flush
// time), a Float64Array (a caller-made snapshot, immune to later
// mutation), or a legacy object array. See _toInterleaved.
export const persistTrades = (tokenKey, trades) => {
  if (!tokenKey || !trades) return
  _tradesFlushQueue.set(tokenKey, trades)
  if (!_tradesFlushTimer) {
    _tradesFlushTimer = setTimeout(() => {
      _tradesFlushTimer = null
      flushTradesNow()
    }, TRADES_FLUSH_INTERVAL_MS)
  }
}

// Public: drain trade queue to disk. APPEND-ONLY: each flush unions the
// in-memory buffer with the existing on-disk record (dedupe by exact
// trade quad), trimmed to the retention window. The previous put()
// REPLACED the record with whatever survived in memory — any in-memory
// loss (prune, reload before restore, token-switch races) was baked
// into disk permanently on the next flush. With the union, disk only
// ever grows within retention; memory state can't damage it.
export const flushTradesNow = async () => {
  if (_tradesFlushQueue.size === 0) return
  const batch = Array.from(_tradesFlushQueue.entries())
  _tradesFlushQueue.clear()
  try {
    const db = await _openDb()
    const cutoff = appNow() - TRADES_RETAIN_MS
    await new Promise((resolve, reject) => {
      const tx = db.transaction(TRADES_STORE_NAME, 'readwrite')
      const store = tx.objectStore(TRADES_STORE_NAME)
      for (const [tokenKey, trades] of batch) {
        // Snapshot the in-memory side to interleaved Float64 ONCE, up front.
        // For a live TradeBuffer this reads its freshest columns at flush
        // time; for a Float64Array/object-array it's the caller's snapshot.
        const f = _toInterleaved(trades)
        const getReq = store.get(tokenKey)
        getReq.onsuccess = () => {
          const rec = getReq.result
          const existing = rec && rec.trades ? rec.trades : null
          const seen = new Set()
          const out = []
          if (existing) {
            for (let i = 0; i < existing.length; i += 4) {
              const time = existing[i]
              if (time < cutoff) continue
              const k = time + '|' + existing[i + 1] + '|' + existing[i + 2] + '|' + existing[i + 3]
              if (seen.has(k)) continue
              seen.add(k)
              out.push(time, existing[i + 1], existing[i + 2], existing[i + 3])
            }
          }
          for (let i = 0; i < f.length; i += 4) {
            const time = f[i]
            if (time < cutoff) continue
            const k = time + '|' + f[i + 1] + '|' + f[i + 2] + '|' + f[i + 3]
            if (seen.has(k)) continue
            seen.add(k)
            out.push(time, f[i + 1], f[i + 2], f[i + 3])
          }
          if (out.length === 0) { store.delete(tokenKey); return }
          store.put({
            tokenKey,
            trades: Float64Array.from(out),
            lastFlushAt: Date.now(),
          })
        }
      }
      tx.oncomplete = () => resolve()
      tx.onerror    = () => reject(tx.error)
      tx.onabort    = () => reject(tx.error)
    })
  } catch (err) {
    console.warn('[candleStore] flushTradesNow failed:', err)
    for (const [k, v] of batch) {
      if (!_tradesFlushQueue.has(k)) _tradesFlushQueue.set(k, v)
    }
  }
}

// Public: wipe a single coin's trade record.
export const clearTrades = async (tokenKey) => {
  if (!tokenKey) return
  try {
    const db = await _openDb()
    await new Promise((resolve, reject) => {
      const tx = db.transaction(TRADES_STORE_NAME, 'readwrite')
      const req = tx.objectStore(TRADES_STORE_NAME).delete(tokenKey)
      req.onsuccess = () => resolve()
      req.onerror   = () => reject(req.error)
    })
  } catch (err) {
    console.warn('[candleStore] clearTrades failed:', err)
  }
}
